import { merge } from "lodash";

import type { AxiosErrorContext } from "./sentry/helpers/get-axios-options";
import type { Hub, Scope, Severity, Primitive } from "@sentry/types";
import type { AxiosError } from "axios";

import { getAxiosOptions } from "./sentry/helpers/get-axios-options";
import { mergeAdditionalData } from "./sentry/helpers/merge-additional-data";
import {
  getSentryNoSampling,
  getSentryWithSampling
} from "./sentry/sentry-clients";

export type SentryOptions = {
  tags?: AxiosErrorContext & Record<string, Primitive>;
  level?: "fatal" | "info" | "warning" | "error" | "log" | "debug";
  extra?: Partial<{
    axios: Omit<AxiosErrorContext, "request_id">;
    additional_data: Record<string, any>;
  }> &
    Record<string, unknown>;
  no_sampling?: boolean;
};

export function buildSentryErrorAndOptions(
  original_error: Error | AxiosError,
  options: SentryOptions = {}
): [Error, SentryOptions] {
  const axios_options = getAxiosOptions(original_error);
  if (axios_options) {
    merge(options, axios_options);
  }

  mergeAdditionalData(original_error, options);

  return [original_error, options];
}

function devLogger(
  error: Error | AxiosError | string,
  options: SentryOptions | undefined
) {
  if (globalThis.__ENV__ === "development") {
    // eslint-disable-next-line no-console
    console.error(error, options);
  }
}

export const SentryFacade = {
  /**
   * AxiosError doesn't have a stack trace.
   *
   * Use a SuperError to wrap axios errors for better reporting.
   *
   * ---
   *
   * Ex: throw new BusbudError.GeneralError().causedBy(axios_error);
   *
   * @param original_err
   * @param options
   */
  captureException: function (
    original_err: Error | AxiosError,
    options: SentryOptions = {}
  ) {
    if (original_err && original_err instanceof Error) {
      const [sentry_error, sentry_options] = buildSentryErrorAndOptions(
        original_err,
        options
      );
      devLogger(sentry_error, sentry_options);
      return captureWithOptions(
        client => client.captureException(sentry_error),
        sentry_options
      );
    }

    // `original_err` is not en Error. Report it as a message.
    else {
      const message = JSON.stringify(original_err).slice(0, 80);
      merge(options, { extra: { error: original_err } });
      devLogger(message, options);
      return this.captureMessage(message, options);
    }
  },

  captureMessage(message: string, options?: SentryOptions) {
    devLogger(message, options);
    return captureWithOptions(
      client => client.captureMessage(message),
      options
    );
  },

  async addBreadcrumb(data: {}) {
    (await getSentryWithSampling()).addBreadcrumb(data);
    (await getSentryNoSampling()).addBreadcrumb(data);
  },

  async configureScope(scope: (scope: Scope) => void) {
    (await getSentryWithSampling()).configureScope(scope);
    (await getSentryNoSampling()).configureScope(scope);
  }
};

async function captureWithOptions(
  captureFn: (c: Awaited<ReturnType<typeof getSentryClient>>) => void,
  options?: SentryOptions
): Promise<void> {
  const client = await getSentryClient(options);

  if (!options) {
    captureFn(client);
    return;
  }

  client.withScope(scope => {
    const { tags, extra, level } = options;

    if (tags && JSON.stringify(tags) !== "{}") {
      scope.setTags(tags);
    }

    if (extra && JSON.stringify(extra) !== "{}") {
      scope.setExtras(extra);
    }

    if (level) {
      scope.setLevel(level as Severity);
    }

    captureFn(client);
  });
}

async function getSentryClient(
  options: SentryOptions | undefined
): Promise<Hub> {
  const sentry =
    options && options.no_sampling === true
      ? await getSentryNoSampling()
      : await getSentryWithSampling();
  return sentry;
}

export type SentryClient = Pick<
  typeof SentryFacade,
  "captureMessage" | "captureException"
>;
export default SentryFacade;
